import React, { useState, useEffect } from 'react';
import CandidateLayout from '../../components/CandidateLayout';
import { candidatesAPI, trabalhoEventualAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, User, Award, Mail, Phone, MapPin, Briefcase, Clock, AlertCircle, CheckCircle } from 'lucide-react';

export default function CandidateProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [categoriasEventual, setCategoriasEventual] = useState([]);
  const [validandoAtividade, setValidandoAtividade] = useState(false);
  const [atividadeValida, setAtividadeValida] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchCategoriasEventual();
  }, []);

  const fetchCategoriasEventual = async () => {
    try {
      const response = await trabalhoEventualAPI.getCategorias();
      setCategoriasEventual(response.data.categorias || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const validarAtividadeOutra = async (atividade) => {
    if (!atividade || atividade.length < 3) {
      setAtividadeValida(null);
      return;
    }
    setValidandoAtividade(true);
    try {
      const response = await trabalhoEventualAPI.validarAtividade(atividade);
      setAtividadeValida(response.data);
      if (!response.data.permitido) {
        toast.error(response.data.motivo);
      }
    } catch (error) {
      setAtividadeValida({ permitido: true });
    } finally {
      setValidandoAtividade(false);
    }
  };

  const handleCategoriaEventualChange = (categoriaId, checked) => {
    const categorias = form.categorias_eventual || [];
    if (checked) {
      setForm({ ...form, categorias_eventual: [...categorias, categoriaId] });
    } else {
      setForm({ ...form, categorias_eventual: categorias.filter(c => c !== categoriaId) });
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await candidatesAPI.getMe();
      setProfile(response.data);
      setForm(response.data);
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await candidatesAPI.updateMe(form);
      setProfile(response.data);
      setEditing(false);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CandidateLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="candidate-profile">
        {/* Profile Header */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <User className="w-10 h-10 text-cyan-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">
                    {profile?.nome || 'Seu Nome'}
                  </h1>
                  <p className="text-lg text-slate-600">{profile?.cargo}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {(profile?.is_pcd || profile?.is_50_plus) && (
                      <Badge className="bg-cyan-100 text-cyan-700">
                        <Award className="w-3 h-3 mr-1" />
                        Elegível a políticas de inclusão
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {!editing ? (
                <Button 
                  variant="outline"
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-btn"
                >
                  Editar Perfil
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setForm(profile);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    className="bg-cyan-600 hover:bg-cyan-700"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-profile-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Profile Details */}
        {editing ? (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Editar Informações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={form.nome || ''}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={form.telefone || ''}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cargo</Label>
                <Input
                  value={form.cargo || ''}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Experiência</Label>
                  <Select
                    value={form.experiencia || ''}
                    onValueChange={(value) => setForm({ ...form, experiencia: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Junior">Junior</SelectItem>
                      <SelectItem value="Pleno">Pleno</SelectItem>
                      <SelectItem value="Senior">Sênior</SelectItem>
                      <SelectItem value="Especialista">Especialista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Localização</Label>
                  <Input
                    value={form.localizacao || ''}
                    onChange={(e) => setForm({ ...form, localizacao: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Resumo</Label>
                <Textarea
                  value={form.resumo || ''}
                  onChange={(e) => setForm({ ...form, resumo: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label>Pessoa com Deficiência (PCD)</Label>
                  <Switch
                    checked={form.is_pcd || false}
                    onCheckedChange={(checked) => setForm({ ...form, is_pcd: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Profissional 50+</Label>
                  <Switch
                    checked={form.is_50_plus || false}
                    onCheckedChange={(checked) => setForm({ ...form, is_50_plus: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Perfil visível para empresas</Label>
                  <span className="text-xs font-medium text-green-700" data-testid="profile-always-visible">Sempre ativo</span>
                </div>
              </div>

              {/* Seção de Trabalho Eventual */}
              <Card className="border-cyan-200 bg-cyan-50/50">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-cyan-600" />
                      <CardTitle className="text-lg text-cyan-800">Serviços Eventuais</CardTitle>
                    </div>
                    <Switch
                      checked={form.disponivel_eventual || false}
                      onCheckedChange={(checked) => setForm({ ...form, disponivel_eventual: checked })}
                      data-testid="toggle-trabalho-eventual"
                    />
                  </div>
                  <p className="text-sm text-cyan-700">
                    Ative para se disponibilizar para trabalhos eventuais (diárias, eventos)
                  </p>
                </CardHeader>
                
                {form.disponivel_eventual && (
                  <CardContent className="pt-0 space-y-4">
                    <div>
                      <Label className="text-cyan-800 mb-3 block">Selecione suas atividades:</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {categoriasEventual.map((cat) => (
                          <div key={cat.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`cat-${cat.id}`}
                              checked={(form.categorias_eventual || []).includes(cat.id)}
                              onCheckedChange={(checked) => handleCategoriaEventualChange(cat.id, checked)}
                            />
                            <label
                              htmlFor={`cat-${cat.id}`}
                              className="text-sm text-slate-700 cursor-pointer"
                            >
                              {cat.nome}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-cyan-800">Outra atividade:</Label>
                      <div className="relative">
                        <Input
                          placeholder="Ex: Jardineiro, Eletricista, etc."
                          value={form.atividade_eventual_outra || ''}
                          onChange={(e) => {
                            setForm({ ...form, atividade_eventual_outra: e.target.value });
                            // Debounce validation
                            clearTimeout(window.validarAtividadeTimeout);
                            window.validarAtividadeTimeout = setTimeout(() => {
                              validarAtividadeOutra(e.target.value);
                            }, 500);
                          }}
                          className={`pr-10 ${atividadeValida && !atividadeValida.permitido ? 'border-red-500' : ''}`}
                          data-testid="input-atividade-outra"
                        />
                        {validandoAtividade && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-cyan-600" />
                        )}
                        {!validandoAtividade && atividadeValida && (
                          atividadeValida.permitido ? (
                            <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                          )
                        )}
                      </div>
                      {atividadeValida && !atividadeValida.permitido && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {atividadeValida.motivo}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        Digite uma atividade não listada acima. Atividades ilícitas serão bloqueadas.
                      </p>
                    </div>
                  </CardContent>
                )}
              </Card>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{profile?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Telefone</p>
                    <p className="font-medium text-slate-900">{profile?.telefone || 'Não informado'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Localização</p>
                    <p className="font-medium text-slate-900">{profile?.localizacao || 'Não informado'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Briefcase className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Experiência</p>
                    <p className="font-medium text-slate-900">{profile?.experiencia}</p>
                  </div>
                </div>
              </div>

              {profile?.resumo && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Resumo Profissional</p>
                  <p className="text-slate-700">{profile.resumo}</p>
                </div>
              )}

              {profile?.disc_profile && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Perfil DISC</p>
                  <Badge variant="outline">{profile.disc_profile}</Badge>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </CandidateLayout>
  );
}
